import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import {
  EnumHelper,
  MessageService,
  AppBarTitleService,
  AppBarActionsService,
  FormHelper,
  AppBarAction,
  BankAccount,
  BankAccountType,
  AppPageService,
  SimpleDialogService,
  CommonActionKey
} from 'common';
import { BrokerService } from '../broker.service';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { readWrite } from '../../user/user-permission/user-permission.data';
import { BrokerData } from '../broker.model';

@UntilDestroy()
@Component({
    selector: 'ifb-broker-bank-account-details',
    templateUrl: './broker-bank-account-details.component.html',
    styleUrls: ['./broker-bank-account-details.component.scss'],
    standalone: false
})
export class BrokerBankAccountDetailsComponent implements OnInit {
  canEditBankAccount = false;
  form: UntypedFormGroup;
  title: string;
  accountType: string;
  saveSub: Subscription;
  data: BrokerBankAccountDetailsComponentData;

  constructor(
    private readonly brokerService: BrokerService,
    private readonly userPermissionService: UserPermissionService,
    private readonly messageService: MessageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly simpleDialogService: SimpleDialogService
  ) {

    this.appBarActionsService.actions = [
      { id: 'cancel', label: 'Cancel', buttonType: 'button' },
      { id: 'delete', label: 'Delete', disabled: true, buttonType: 'button' },
      { id: 'save', label: 'Save', disabled: true, buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' }
    ];

    this.form = this.formBuilder.group({
      bankName: [undefined, Validators.required],
      accountNumber: [{ value: undefined, disabled: true }, Validators.required],
      routingNumber: [undefined, Validators.required],
      disbursementBankAccount: undefined,
      paymentBankAccount: undefined
    });
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this))
      .subscribe(this.initData.bind(this));

    this.appBarActionsService.invoking.pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));

    this.userPermissionService.granted([readWrite('servicing-bank-accounts')])
      .subscribe(res => {
        this.canEditBankAccount = res;
        if (res && this.data.bankAccount) {
          this.appBarActionsService.enable('delete', true);
        } else if (!res) {
          this.form.disable();
        }
      });

    this.userPermissionService.granted([readWrite('servicing-entities-sensitive-data')])
      .subscribe(res => {
        if (res)
          this.form.get('accountNumber').enable();
      });

    this.watchFormChanges();
  }

  private watchFormChanges(): void {
    this.form.valueChanges.subscribe(() => {
      this.enableSaveAction();
    });
  }

  private initData(data: BrokerBankAccountDetailsComponentData): void {
    this.data = data;

    this.form.reset({
      name: data.broker ? data.broker.name : '',
      bankName: data.bankAccount ? data.bankAccount.bankName : '',
      accountNumber: data.bankAccount ? data.bankAccount.accountNumber : '',
      routingNumber: data.bankAccount ? data.bankAccount.routingNumber : '',
    });

    this.accountType = this.data.bankAccount ? EnumHelper.getNameFromValue(BankAccountType, this.data.bankAccount.accountType) : null;

    this.title = this.data.bankAccount ? (this.data.bankAccount.bankName ? this.data.bankAccount.bankName : '')
      + ' ' + (this.accountType ? this.accountType : '') + ' ' + this.sanitize(this.data.bankAccount.accountNumber) : '';
    this.appBarTitleService.title = this.data.bankAccount ? this.title : 'Add bank';

    if (!this.data.bankAccount)
      this.appBarActionsService.hide('delete', true);
  }

  private sanitize(s: string): string {
    return s ? '*' + s.substring(s.length - 4) : null;
  }

  cancel(): void {
    this.appPageService.back();
  }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);

    if (this.saveSub)
      return;

    if (!this.data || !this.data.broker || !this.form.valid) {
      return;
    }

    const message = `Save changes to ${this.data.broker.name}?`;
    this.simpleDialogService.openSimpleConfirmDialog(message, "Confirm")
    .subscribe((result: CommonActionKey) => {
      if (result !== CommonActionKey.Confirm) {
        return;
      }
      const command: BankAccount = {
        id: this.data.bankAccount ? this.data.bankAccount.id : null,
        bankName: this.form.controls.bankName.value,
        routingNumber: this.form.controls.routingNumber.value,
        accountNumber: this.form.controls.accountNumber.value,
      };

      if (this.data.bankAccount) {
        this.saveSub = this.brokerService
          .updateBrokerBankAccount(this.data.broker.id, command)
          .subscribe(this.saveSuccessHandler.bind(this), this.saveErrorHandler.bind(this));
      } else {
        this.saveSub = this.brokerService
          .createBrokerBankAccount(this.data.broker.id, command)
          .subscribe(this.saveSuccessHandler.bind(this), this.saveErrorHandler.bind(this));
      }
    });
  }

  delete(): void {
    if (!this.data.bankAccount || !this.data.bankAccount.id || !this.canEditBankAccount)
      return;

    const message = `Delete ${this.title}?`;
    this.simpleDialogService.openSimpleConfirmDialog(message, "Confirm")
    .subscribe((result: CommonActionKey) => {
      if (result === CommonActionKey.Confirm) {
        this.brokerService.deleteBrokerBankAccount(this.data.broker.id, this.data.bankAccount.id)
        .subscribe(() => {
          this.router.navigate([`/broker/${this.data.broker.id}/banks`], { replaceUrl: true });
        });
      }
    });
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error);
  }

  private saveSubClear(): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveSuccessHandler(): void {
    this.saveSubClear();
    this.appPageService.back();
  }

  private enableSaveAction(): void {
    if (this.form.dirty && this.canEditBankAccount) {
      this.appBarActionsService.enable('save', true);
    }
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }
}

export interface BrokerBankAccountDetailsComponentData {
  broker?: BrokerData;
  bankAccount?: BankAccount;
}
